import * as Sentry from '@sentry/vue'
import configs from '@/config'
import CrossDomainHttpClient from '@/services/crossDomainHttp'
import UnleashService from '@/services/unleash'
import { trackAIInsights } from '@/business-logic/ai-tracking'

const FETCH_AI_INSIGHTS_STATUS = {
  IDLE: 'idle',
  LOADING: 'loading',
  SUCCESS: 'success',
  ERROR: 'error',
}

const AI_INSIGHTS_TIMEOUT_MS = 45000

/**
 * Generates authentication headers for API requests
 * @returns {Object} Headers object with Authorization token if available
 */
const getAuthHeaders = () => {
  // Simple cookie getter for ahaToken
  const getCookie = (name) => {
    const value = `; ${document.cookie}`
    const parts = value.split(`; ${name}=`)
    return parts.length === 2 ? parts.pop().split(';').shift() : null
  }

  const token = getCookie('ahaToken')
  return token ? { Authorization: `Bearer ${token}` } : {}
}

const moduleState = {
  insights: [],
  status: FETCH_AI_INSIGHTS_STATUS.LOADING,
}

const getters = {
  insights: (state) => state.insights,
  status: (state) => state.status,
}

const mutations = {
  /* eslint-disable no-param-reassign */
  SET_INSIGHTS(state, insights) {
    state.insights = insights
  },
  SET_STATUS(state, status) {
    state.status = status
  },
  /* eslint-enable no-param-reassign */
}

const actions = {
  async fetchAIInsightsAction({ commit, rootState }, { presentationId, sessionId, forceRefresh = false }) {
    const userId = rootState.currentUser?.id
    const lang = rootState.presenterLocale || 'en'
    const storageKey = `${userId}-${presentationId}-${sessionId}-${lang}-genai-analysis`

    if (!forceRefresh) {
      const cachedInsights = localStorage.getItem(storageKey)
      if (cachedInsights) {
        try {
          const insightsArray = JSON.parse(cachedInsights)
          commit('SET_INSIGHTS', insightsArray)
          commit('SET_STATUS', FETCH_AI_INSIGHTS_STATUS.SUCCESS)
          return
        } catch (e) {
          localStorage.removeItem(storageKey)
        }
      }
    }

    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), AI_INSIGHTS_TIMEOUT_MS)

    try {
      trackAIInsights({ stage: 'started' })

      commit('SET_STATUS', FETCH_AI_INSIGHTS_STATUS.LOADING)
      const useAhaaiv2 = Boolean(configs.externalApis.aiInsightsBaseV2)
        && UnleashService.getVariant('AHA-42863-ahaaiv2-routing').name === 'treatment'
      const baseUrl = useAhaaiv2 ? configs.externalApis.aiInsightsBaseV2 : configs.externalApis.aiInsightsBase
      const url = `${baseUrl}/${presentationId}/session/${sessionId}/`
      const response = await CrossDomainHttpClient.get(url, {}, getAuthHeaders(), {
        signal: controller.signal,
      })

      const insightsArray = response.body?.insights || []
      commit('SET_INSIGHTS', insightsArray)
      commit('SET_STATUS', FETCH_AI_INSIGHTS_STATUS.SUCCESS)

      trackAIInsights({ stage: 'succeeded' })

      // Store in local storage
      if (insightsArray.length > 0) {
        localStorage.setItem(storageKey, JSON.stringify(insightsArray))
      }
    } catch (error) {
      const isTimeout = error.name === 'AbortError'
      const reportedError = isTimeout
        ? new Error(`AI insights request timed out after ${AI_INSIGHTS_TIMEOUT_MS / 1000}s`)
        : error

      trackAIInsights({ stage: 'failed', error: reportedError })

      commit('SET_STATUS', FETCH_AI_INSIGHTS_STATUS.ERROR)
      Sentry.captureException(reportedError, {
        tags: {
          feature: 'ai-insights',
          context: 'fetch-ai-insights-action',
          isTimeout: isTimeout ? 'true' : 'false',
        },
      })
      throw reportedError
    } finally {
      clearTimeout(timeoutId)
    }
  },
}

export default {
  namespaced: true,
  state: moduleState,
  getters,
  mutations,
  actions,
}
